import axios from "axios";
import { SystemZone } from "luxon";
import posthog from "posthog-js";

import { BaseApiDatasource, BaseApiDatasourceSpec } from "@/io/datasource/BaseApiDatasource.ts";

class ApiDatasource extends BaseApiDatasource {
  unauthorizedInterceptor: number | undefined;
  refreshPromise: Promise<void> | undefined;

  createInstance = (token?: string) => {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_APP_API_URL || "/api/v1",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
        Version: 2, // this is an API-specific value
        Timezone: SystemZone.instance.name,
      },
    });
    this.axiosInstance.interceptors.request.use(config => {
      const sessionId = posthog.get_session_id();
      if (sessionId) {
        config.headers["x-posthog-session-id"] = sessionId;
      }
      return config;
    });
  };

  onUnauthorized = (refresh: () => Promise<void>) => {
    const instance = this.axiosInstance;
    if (!instance) {
      return;
    }
    this.ejectUnauthorizedInterceptor();

    this.unauthorizedInterceptor = instance.interceptors.response.use(undefined, async error => {
      if (error.response?.status === 401 && !this.refreshPromise) {
        this.refreshPromise = refresh();
        await this.refreshPromise;
        this.refreshPromise = undefined;
      }

      return Promise.reject(error);
    });
  };

  destroyInstance = () => {
    if (this.axiosInstance) {
      this.ejectUnauthorizedInterceptor();
      this.axiosInstance = undefined;
    }
  };

  private ejectUnauthorizedInterceptor = () => {
    if (this.axiosInstance && this.unauthorizedInterceptor !== undefined) {
      this.axiosInstance.interceptors.response.eject(this.unauthorizedInterceptor);
    }
    this.unauthorizedInterceptor = undefined;
  };
}

export type ApiDataSourceSpec = BaseApiDatasourceSpec;

export const apiDataSource = new ApiDatasource();

/**
 * Unauthenticated sibling for the session endpoints (refresh, logout): no Bearer header,
 * and no 401 handling — a 401 there means a bad refresh token, not a stale session.
 */
export const publicApiDataSource = new ApiDatasource();
publicApiDataSource.createInstance();

export const createApiDatasource = () => new ApiDatasource();
